import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApolloError, useApolloClient, useMutation } from '@apollo/client';
import { type UniqueIdentifier } from '@dnd-kit/core';
import { cloneDeep } from 'lodash';
import cuid from 'cuid';

import MenuTitle from 'components/Title/MenuTitle';
import DoshiiMenuLogic from 'components/DoshiiMenuLogic/DoshiiMenuLogic';
import {
  MenuFragment as MENU_FRAGMENT,
  PublishMenuMutation as PUBLISH_MENU_MUTATION,
  SaveMenuMutation as SAVE_MENU_MUTATION
} from 'queries/Menu';
import { mockVenue } from 'utils/mockData';
import {
  addAppsToProductVariantsByApp,
  categorySort,
  getThemeClassNameByIndex,
  listAppsInString,
  posProductToMMProduct,
  sanitiseUrlParam
} from 'utils';
import State from 'utils/State';
import { getFormattedCategories } from 'utils/Menu';
import { usePrompt } from 'utils/usePrompt';
import { type Items, type QueuedProductImageUpdate, OptionSource, SnackBarTypeEnum } from 'types';
import { CategoryContext } from 'components/CategoryList/CategoryContext';
import TestDragSimulator from 'components/SingleMenu/TestDragSimulator';
import type {
  App,
  Category,
  CategoryInput,
  DeleteProductImageMutation,
  DeleteProductImageMutationVariables,
  Menu,
  MenuInput,
  MenuOption,
  PosProduct,
  PosProductQuery,
  PosProductQueryVariables,
  Product,
  ProductLinkQuery,
  ProductLinkQueryVariables,
  PublishMenuMutation,
  PublishMenuMutationVariables,
  SaveMenuMutation,
  SaveMenuMutationVariables,
  UpdateProductImageMutation,
  UpdateProductImageMutationVariables,
  Venue
} from 'generated/graphql';
import { useModalContext } from 'common/Context/ModalContext';
import {
  DeleteProductImageMutation as DELETE_PRODUCT_IMAGE_MUTATION,
  posProductQuery as POS_PRODUCT_QUERY,
  ProductLinkQuery as PRODUCT_LINK_QUERY,
  UpdateProductImageMutation as UPDATE_PRODUCT_IMAGE_MUTATION
} from 'queries/Product';
import { useAppContext } from 'AppContext';
import useScreenWidthQuery from 'common/Hooks/UseScreenWidthQuery';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import * as GraphqlTypecastWorkaround from 'utils/GraphqlTypecastWorkaround';
import type { AppFragment, ProductFragment } from 'types/graphqlFragment';
import { useOperatingHours } from 'utils/useOperatingHours';
import { DEFAULT_TIMEZONE } from 'consts';
import { useVenueContext } from './Main';
import { deepDiffWithIgnores } from '../utils/Diff';
import {
  compileChanges,
  convertToMenuInput,
  type PosProductImageMap,
  validateProductInputs
} from '../utils/menuConversion';
import { WindowBreakpoints } from '../utils/DocumentHelper';
import { AppFragment as APP_FRAGMENT } from '../queries/App';
import { venueHasBrands } from '../utils/brand';

const { headerCustomTheme } = State;

type CalculatedMenuStatesBase = [sortedMenuIndex: number, currentMenuIndex: number, draftMenuIndex: number];
// ...Base, publishedMenu, draftMenu
type CalculatedMenuStates =
  | [...CalculatedMenuStatesBase, null, Menu | null]
  | [...CalculatedMenuStatesBase, Menu, Menu | undefined];

type Props = {
  isTest?: boolean;
};

export const returnUnloadEvent = (event: BeforeUnloadEvent): string => {
  event.preventDefault();
  event.returnValue = '';

  const confirmationMessage = 'You have unsaved changes on this menu.';

  event.returnValue = confirmationMessage;
  return confirmationMessage;
};

const SingleMenu: React.FC<Props> = ({ isTest }) => {
  const { menuId, itemId } = useParams();
  const navigate = useNavigate();

  const sanitisedMenuId = sanitiseUrlParam(menuId);

  const {
    draftMenus,
    setDraftMenus,
    queuedProductImageUpdates,
    updateQueuedProductImageUpdates,
    modifiedMenuIds,
    setModifiedMenuIds
  } = useAppContext();

  const { openModal, openSnackBar, closeSnackBar, openStickyHeader, closeStickyHeader } = useModalContext();
  const [infoSnackBarId, setInfoSnackBarId] = useState<string | null>(null);
  const { currentVenue, isLoading } = useVenueContext();
  const [publishMenuMutation] = useMutation<PublishMenuMutation, PublishMenuMutationVariables>(PUBLISH_MENU_MUTATION);
  const [saveMenuMutation] = useMutation<SaveMenuMutation, SaveMenuMutationVariables>(SAVE_MENU_MUTATION);
  const [updateProductImageMutation] = useMutation<UpdateProductImageMutation, UpdateProductImageMutationVariables>(
    UPDATE_PRODUCT_IMAGE_MUTATION
  );
  const [deleteProductImageMutation] = useMutation<DeleteProductImageMutation, DeleteProductImageMutationVariables>(
    DELETE_PRODUCT_IMAGE_MUTATION
  );
  const { cache, query } = useApolloClient();

  const [sortedMenuIndex, currentMenuIndex, draftMenuIndex, publishedMenu, draftMenu] =
    useMemo<CalculatedMenuStates>((): CalculatedMenuStates => {
      if (isLoading) {
        return [0, 0, 0, null, mockVenue.menus[0]];
      }

      if (!currentVenue?.menus?.length) {
        return [0, 0, 0, null, null];
      }

      const sortedMenus = [...currentVenue.menus].sort((a, b) => {
        if (b.apps.length - a.apps.length !== 0) return b.apps.length - a.apps.length;
        return a.name.localeCompare(b.name);
      });

      const sortedMenuIndex = sortedMenus.findIndex((menu) => menu.mmId === sanitisedMenuId);
      const currentMenuIndex = currentVenue?.menus.findIndex((menu) => menu.mmId === sanitisedMenuId);

      const draftMenu = draftMenus?.find((menu) => menu.mmId === sanitisedMenuId);
      const draftMenuIndex = draftMenus?.findIndex((menu) => menu.mmId === sanitisedMenuId);
      return [sortedMenuIndex, currentMenuIndex, draftMenuIndex, sortedMenus[sortedMenuIndex], draftMenu];
    }, [currentVenue?.menus, draftMenus, isLoading, sanitisedMenuId]);
  const [innerWidth] = useScreenWidthQuery();
  const [newlyAddedPosProducts, setNewlyAddedPosProducts] = useState<Product[]>([]);
  const [newlyAddedPosProductImageMap, setNewlyAddedPosProductImageMap] = useState<PosProductImageMap>({});

  const { isOperatingHoursSupported, hasCommonHoursAcrossAllMenusBeenApplied } = useOperatingHours({
    currentVenue,
    appId: currentVenue?.menus?.find((menu) => menu.mmId === sanitisedMenuId)?.apps[0].id
  });

  const neverPublished = !!draftMenu && draftMenu.neverPublished;

  const menuBusyUntil = currentVenue?.menus?.find((menu) => menu.mmId === sanitisedMenuId)?.busyUntil;
  const showBusyUntilChip = menuBusyUntil ? new Date(menuBusyUntil) > new Date() : false;
  const busyUntil =
    showBusyUntilChip && menuBusyUntil
      ? new Date(menuBusyUntil).toLocaleTimeString('en-AU', {
          timeZone: currentVenue?.timezone || DEFAULT_TIMEZONE,
          hour: '2-digit',
          minute: '2-digit',
          second: undefined // Exclude seconds
        })
      : null;

  useEffect(() => {
    if (!draftMenu && publishedMenu) {
      setDraftMenus([cloneDeep(publishedMenu)]);
    }
  }, [publishedMenu, draftMenu, setDraftMenus]);

  const setDraftMenu = useCallback(
    (updatedMenu: Menu | null) => {
      const draftMenusCopy = cloneDeep(draftMenus);
      if (draftMenusCopy && draftMenuIndex !== undefined && draftMenuIndex >= 0 && updatedMenu) {
        draftMenusCopy[draftMenuIndex] = updatedMenu;
        setDraftMenus(draftMenusCopy);
      }
    },
    [draftMenuIndex, draftMenus, setDraftMenus]
  );

  const [changedCategoryIds, setChangedCategoryIds] = useState<string[]>([]);
  const [changedProductIds, setChangedProductIds] = useState<string[]>([]);

  const changedCategoryIdsRef = useRef(changedCategoryIds);
  changedCategoryIdsRef.current = changedCategoryIds;

  const changedProductIdsRef = useRef(changedProductIds);
  changedProductIdsRef.current = changedProductIds;

  useEffect(() => {
    const clonedModifiedMenuIds = cloneDeep(modifiedMenuIds);
    const index = clonedModifiedMenuIds.findIndex((id) => id === sanitisedMenuId);
    if ((changedProductIds.length || changedCategoryIds.length) && index === -1) {
      clonedModifiedMenuIds.push(sanitisedMenuId);
      setModifiedMenuIds(clonedModifiedMenuIds);
    } else if (!changedProductIds.length && !changedCategoryIds.length && index > -1) {
      clonedModifiedMenuIds.splice(index, 1);
      setModifiedMenuIds(clonedModifiedMenuIds);
    }
  }, [changedCategoryIds, changedProductIds, modifiedMenuIds, sanitisedMenuId, setModifiedMenuIds]);

  const addChangedCategoryIds = useCallback(
    (ids: string[]): void => {
      const _changedCategoryIds = [...changedCategoryIdsRef.current];

      ids.forEach((id) => {
        if (!_changedCategoryIds.includes(id)) {
          _changedCategoryIds.push(id);
        }
      });
      setChangedCategoryIds(_changedCategoryIds);
    },
    [setChangedCategoryIds]
  );

  const themeClassName = getThemeClassNameByIndex(sortedMenuIndex);

  const handleBeforeUnload = (event: BeforeUnloadEvent): string | undefined => {
    if (changedCategoryIdsRef.current.length || changedProductIdsRef.current.length) {
      return returnUnloadEvent(event);
    }
    return undefined;
  };

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!publishedMenu && !isLoading) {
      if (!currentVenue) navigate('/');
      else navigate(`/venue/${currentVenue.hashedId}`);
    } else {
      headerCustomTheme(themeClassName);
    }
  }, [isLoading, currentVenue, publishedMenu, navigate, themeClassName]);

  const categories = useMemo(() => {
    return draftMenu ? draftMenu.categories : [];
  }, [draftMenu]);

  const [items, setItems] = useState<Items>({});
  const [containers, setContainers] = useState(Object.keys(items) as UniqueIdentifier[]);

  const resetDragOrder = useCallback(() => {
    setDraftMenu(cloneDeep(publishedMenu));
    setChangedCategoryIds([]);
    setChangedProductIds([]);
  }, [publishedMenu, setDraftMenu]);

  useEffect(() => {
    if (draftMenu) {
      setItems(getFormattedCategories(draftMenu));
      setContainers([...draftMenu.categories].sort(categorySort).map((category) => category.mmId));
    }
  }, [draftMenu, draftMenus]);

  const addNewInlineProduct = useCallback(
    async (product: PosProduct | Product, categoryId: UniqueIdentifier) => {
      if (!draftMenu || !publishedMenu || !currentVenue?.hashedId) return;
      let addingPosProduct: boolean;
      let productToBeAdded: Product;
      if (product.__typename === 'POSProduct') {
        addingPosProduct = true;
        productToBeAdded = posProductToMMProduct(product, `doshiinewpos${product.posId}`, draftMenu.apps);
        const clonedNewlyAddedPosProducts = cloneDeep(newlyAddedPosProducts);
        if (
          !clonedNewlyAddedPosProducts.some((p) =>
            p.customPosId ? p.customPosId === product.customPosId : p.posId === product.posId
          )
        ) {
          clonedNewlyAddedPosProducts.push(productToBeAdded);
          setNewlyAddedPosProducts(clonedNewlyAddedPosProducts);
        }
      } else if (product.__typename === 'Product') {
        addingPosProduct = false;
        if (!product.variablesByApp.some((variable) => publishedMenu.apps.some((a) => a.id === variable.app.id))) {
          const productLinkResult = await query<ProductLinkQuery, ProductLinkQueryVariables>({
            query: PRODUCT_LINK_QUERY,
            variables: {
              posId: product.posId,
              appId: product.variablesByApp[0].app.id,
              locationId: currentVenue.hashedId
            }
          });
          const {
            data: { posProduct }
          } = await query<PosProductQuery, PosProductQueryVariables>({
            query: POS_PRODUCT_QUERY,
            variables: {
              posId: product.posId,
              locationId: currentVenue.hashedId
            }
          });
          if (!posProduct) {
            // eslint-disable-next-line no-console
            console.error(`Expected POS Product query to return posProduct`);
            return;
          }

          const clonedNewlyAddedPosProducts = cloneDeep(newlyAddedPosProducts);
          const productHasSurcharge = product.variablesByApp.some(
            (variable) => variable.surcharge && variable.surcharge.amount > 0
          );
          const menuHasSurcharge = draftMenu.surcharge && draftMenu.surcharge.amount > 0;
          const posPrice = productHasSurcharge || menuHasSurcharge ? posProduct.unitPrice : null;

          if (
            !clonedNewlyAddedPosProducts.some((p) =>
              p.customPosId ? p.customPosId === product.customPosId : p.posId === product.posId
            )
          ) {
            clonedNewlyAddedPosProducts.push(
              posProductToMMProduct(posProduct, `doshiiexistingpos${product.posId}`, draftMenu.apps)
            );
            setNewlyAddedPosProducts(clonedNewlyAddedPosProducts);
            if (posProduct.imageUri) {
              setNewlyAddedPosProductImageMap((prev) =>
                posProduct.imageUri ? { ...prev, [posProduct.posId]: posProduct.imageUri } : { ...prev }
              );
            }
          }
          const optionsWithSource = product.options.map((option): MenuOption => {
            const optionWithSource = productLinkResult.data?.productLink?.options?.find(
              (o) => o.posId === option.posId
            );
            return {
              ...option,
              source: optionWithSource?.source || OptionSource.POS_PRODUCT
            };
          });
          productToBeAdded = {
            ...addAppsToProductVariantsByApp(
              { ...product, mmId: `doshiinewapp${product.mmId}`, options: optionsWithSource },
              publishedMenu.apps,
              posPrice
            )
          };
        } else {
          productToBeAdded = product;
        }
      } else return;
      const getExistingProductInMenu = (mmId: string): ProductFragment | null => {
        return draftMenu?.products.find((product) => product.mmId === mmId) || null;
      };

      const existingProductInMenu = getExistingProductInMenu(productToBeAdded.mmId);
      const existingProduct = addingPosProduct ? null : existingProductInMenu;
      const clonedCategories = cloneDeep(draftMenu.categories);

      const categoryIndex = clonedCategories.map((category) => category.mmId).indexOf(categoryId.toString());
      if (categoryIndex === -1) return;
      const clonedProducts = cloneDeep(draftMenu.products);
      if (!existingProduct) {
        clonedProducts.push(productToBeAdded);
      }

      if (productToBeAdded.customPosId) {
        clonedCategories[categoryIndex].productIds.push(productToBeAdded.customPosId);
      } else {
        clonedCategories[categoryIndex].productIds.push(productToBeAdded.posId);
      }

      setDraftMenu({
        ...draftMenu,
        categories: clonedCategories,
        products: clonedProducts
      });

      const updatedItem = [...items[categoryId], productToBeAdded.posId];

      setItems({
        ...items,
        [categoryId]: updatedItem
      });
    },
    [draftMenu, publishedMenu, currentVenue?.hashedId, setDraftMenu, items, newlyAddedPosProducts, query]
  );

  /*
    This section allows us to detect changes in the menu and add the changed category ids and products to the tracking states
    */
  useEffect((): void => {
    if (isLoading || !publishedMenu || !draftMenu) return;
    const sortedDraftCategories = cloneDeep(draftMenu.categories || []).sort(categorySort);
    const sortedPublishedCategories = cloneDeep(publishedMenu?.categories || []).sort(categorySort);

    const changedCategories: string[] = [];
    sortedDraftCategories.forEach((draftCategory, index) => {
      if (draftCategory.mmId === 'UNCATEGORISED') return;
      const publishedCategory = index < sortedPublishedCategories.length && sortedPublishedCategories[index];
      if (
        !publishedCategory ||
        deepDiffWithIgnores(draftCategory, publishedCategory, ['displayOrder', 'mmId']).length
      ) {
        changedCategories.push(draftCategory.mmId);
      }
    });
    sortedPublishedCategories.forEach((publishedCategory) => {
      if (!sortedDraftCategories.find((category) => category.mmId === publishedCategory.mmId)) {
        changedCategories.push(publishedCategory.mmId);
      }
    });
    setChangedCategoryIds(changedCategories);

    const clonedDraftProducts = cloneDeep(draftMenu.products || []);
    const clonedPublishedProducts = cloneDeep(publishedMenu?.products || []);

    const changedProducts: string[] = [];
    clonedDraftProducts.forEach((draftProduct) => {
      const publishedProduct = clonedPublishedProducts.find((product) => product.posId === draftProduct.posId);
      if (!publishedProduct || deepDiffWithIgnores(draftProduct, publishedProduct, ['mmId', 'source']).length) {
        changedProducts.push(draftProduct.mmId);
      }
    });
    clonedPublishedProducts.forEach((publishedProduct) => {
      if (!clonedDraftProducts.find((product) => product.posId === publishedProduct.posId)) {
        changedProducts.push(publishedProduct.mmId);
      }
    });
    setChangedProductIds(changedProducts);
  }, [isLoading, draftMenu, publishedMenu]);

  const addNewCategory = useCallback(
    (name: string): void => {
      if (!draftMenu) return;

      const maxDisplayOrder = Math.max(...draftMenu.categories.map((category) => category.displayOrder));
      const newId = `doshiinew${cuid()}`;

      const newCategory: Category = {
        __typename: 'Category',
        category: name,
        productIds: [],
        mmId: newId,
        description: '',
        displayOrder: maxDisplayOrder + 1,
        imageUri: null,
        idsByApp: draftMenu.apps.map((app) => ({
          app,
          id: newId
        }))
      };

      const clonedMenu = cloneDeep(draftMenu);
      clonedMenu.categories.push(newCategory);

      setDraftMenu(clonedMenu);

      setContainers([...containers, newCategory.mmId]);
      setItems({
        ...items,
        [newCategory.mmId]: []
      });
    },
    [draftMenu, setDraftMenu, containers, items]
  );

  const updateCategory = useCallback(
    (categoryInput: CategoryInput): void => {
      const { idsByApp } = categoryInput;
      if (!draftMenu) return;

      const clonedCategories = cloneDeep(draftMenu.categories);
      const currentCategoryIndex = clonedCategories.findIndex(
        (category) => !deepDiffWithIgnores(category.idsByApp, idsByApp).length
      );
      if (currentCategoryIndex === -1) return;

      const currentCategory = clonedCategories[currentCategoryIndex];

      const newCategory: Category = {
        ...currentCategory,
        ...(categoryInput.category && { category: categoryInput.category }),
        ...(categoryInput.displayOrder && { displayOrder: categoryInput.displayOrder }),
        ...(categoryInput.description && { description: categoryInput.description }),
        ...(categoryInput.productIds && { productIds: categoryInput.productIds }),
        ...(categoryInput.imageUri && { imageUri: categoryInput.imageUri })
      };
      // Don't add empty string if field is already nullish. nullish is good
      const publishedCategory = publishedMenu?.categories.find(
        (category) => !deepDiffWithIgnores(category.idsByApp, idsByApp).length
      );
      if (publishedCategory) {
        if (
          newCategory.description === ' ' &&
          (publishedCategory.description === null || publishedCategory.description === undefined)
        ) {
          newCategory.description = null;
        }
      }

      clonedCategories.splice(currentCategoryIndex, 1, newCategory);

      const newMenu = {
        ...draftMenu,
        categories: clonedCategories
      };

      setDraftMenu(newMenu);
    },
    [draftMenu, publishedMenu?.categories, setDraftMenu]
  );

  const reorderCategories = useCallback(
    (newIndex: number, previousIndex: number): void => {
      if (!draftMenu) return;

      const clonedCurrentCategories = cloneDeep(draftMenu.categories);
      const sortedCurrentCategories = clonedCurrentCategories.sort(categorySort);
      sortedCurrentCategories.splice(newIndex, 0, sortedCurrentCategories.splice(previousIndex, 1)[0]);

      sortedCurrentCategories.forEach((category, index) => {
        sortedCurrentCategories[index].displayOrder = index + 1;
      });

      const newMenu = {
        ...draftMenu,
        categories: sortedCurrentCategories
      };
      setDraftMenu(newMenu);
    },
    [draftMenu, setDraftMenu]
  );

  const reorderProducts = useCallback(
    (
      draftItemId: UniqueIdentifier,
      newIndex: number,
      previousIndex: number,
      previousCategoryMmId: UniqueIdentifier,
      newCategoryMmId?: UniqueIdentifier
    ): void => {
      if (!draftMenu) return;

      const clonedDraftCategories = cloneDeep(draftMenu.categories);
      const previousDraftCategory = clonedDraftCategories.find((category) => category.mmId === previousCategoryMmId);
      if (!previousDraftCategory) return;

      const productId = previousDraftCategory.productIds[previousIndex];
      if (!productId) return;

      if (newCategoryMmId) {
        const draftNewCategory = clonedDraftCategories.find((category) => category.mmId === newCategoryMmId);
        if (!draftNewCategory) return;
        const movingIntoUncategorised =
          previousDraftCategory.mmId !== 'UNCATEGORISED' && draftNewCategory.mmId === 'UNCATEGORISED';
        if (draftNewCategory.productIds.find((id) => id === productId) || movingIntoUncategorised) {
          const clonedItems = cloneDeep(items);
          clonedItems[newCategoryMmId].splice(newIndex, 1);
          clonedItems[previousCategoryMmId].splice(previousIndex, 0, draftItemId);
          setItems(clonedItems);
          return;
        }
        draftNewCategory.productIds.splice(newIndex, 0, previousDraftCategory.productIds.splice(previousIndex, 1)[0]);
      } else {
        previousDraftCategory.productIds.splice(
          newIndex,
          0,
          previousDraftCategory.productIds.splice(previousIndex, 1)[0]
        );
      }
      const newMenu = {
        ...draftMenu,
        categories: clonedDraftCategories
      };
      setDraftMenu(newMenu);
    },
    [draftMenu, setDraftMenu, items]
  );

  const deleteProduct = useCallback(
    (categoryMmId: UniqueIdentifier, productToDelete: Product) => {
      if (!draftMenu) return;
      const clonedDraftProducts = cloneDeep(draftMenu.products);
      const clonedDraftCategories = cloneDeep(draftMenu.categories);
      let isExistsInOtherCategories = false;
      const newCategories = clonedDraftCategories.map((category) => {
        const productIndex = category.productIds.findIndex((id) =>
          productToDelete.customPosId ? id === productToDelete.customPosId : id === productToDelete.posId
        );
        if (productIndex !== -1 && category.mmId === categoryMmId) {
          category.productIds.splice(productIndex, 1);
        }
        if (productIndex !== -1 && category.mmId !== categoryMmId) {
          isExistsInOtherCategories = true;
        }
        return category;
      });

      if (!isExistsInOtherCategories) {
        const productIndex = clonedDraftProducts.findIndex((p) =>
          p.customPosId ? p.customPosId === productToDelete.customPosId : p.posId === productToDelete.posId
        );
        if (productIndex !== -1) {
          clonedDraftProducts.splice(productIndex, 1);
        }

        const clonedNewlyAddedPosProducts = cloneDeep(newlyAddedPosProducts);
        const newlyAddedPosProductIndex = clonedNewlyAddedPosProducts.findIndex((p) =>
          p.customPosId ? p.customPosId === productToDelete.customPosId : p.posId === productToDelete.posId
        );
        if (newlyAddedPosProductIndex !== -1) {
          clonedNewlyAddedPosProducts.splice(newlyAddedPosProductIndex, 1);
          setNewlyAddedPosProducts(clonedNewlyAddedPosProducts);
        }
      }

      setDraftMenu({
        ...draftMenu,
        categories: newCategories,
        products: clonedDraftProducts
      });

      const clonedItems = cloneDeep(items);
      const itemIndex =
        clonedItems[categoryMmId] &&
        clonedItems[categoryMmId].findIndex((id) =>
          id.toString().startsWith(productToDelete.customPosId || productToDelete.posId)
        );
      if (itemIndex && itemIndex !== -1) {
        clonedItems[categoryMmId].splice(itemIndex, 1);
      }
      setItems(clonedItems);
    },
    [draftMenu, items, newlyAddedPosProducts, setDraftMenu]
  );

  const deleteCategory = useCallback(
    (categoryMmId: UniqueIdentifier) => {
      if (!draftMenu) return;
      const clonedDraftProducts = cloneDeep(draftMenu.products);
      const clonedDraftCategories = cloneDeep(draftMenu.categories);
      const categoryToDeleteIndex = clonedDraftCategories.findIndex((category) => category.mmId === categoryMmId);
      if (categoryToDeleteIndex !== -1) {
        clonedDraftCategories[categoryToDeleteIndex].productIds.forEach((productId) => {
          const otherCategory = clonedDraftCategories.find(
            (category) => category.mmId !== categoryMmId && category.productIds.includes(productId)
          );
          if (!otherCategory) {
            clonedDraftProducts.splice(
              clonedDraftProducts.findIndex((product) => product.posId === productId),
              1
            );
          }
        });
        clonedDraftCategories.splice(categoryToDeleteIndex, 1);
      }

      setDraftMenu({
        ...draftMenu,
        categories: clonedDraftCategories,
        products: clonedDraftProducts
      });
      const clonedContainers = cloneDeep(containers);
      const containerIndex = clonedContainers.findIndex((container) => container === categoryMmId);
      if (containerIndex !== -1) {
        clonedContainers.splice(containerIndex, 1);
      }
      setContainers(clonedContainers);
      const clonedItems = cloneDeep(items);
      delete clonedItems[categoryMmId];
      setItems(clonedItems);
    },
    [containers, draftMenu, items, setDraftMenu]
  );

  usePrompt(
    'You have unsaved changes on this menu. Leave anyway?',
    resetDragOrder,
    (!!changedCategoryIds.length || !!changedProductIds.length) && !hasCommonHoursAcrossAllMenusBeenApplied
  );

  const [collapsedCategories, setCollapsedCategories] = useState<string[]>([]);

  const handleToggleCategoryCollapse = (id: string): void => {
    const _collapsedCategories = [...collapsedCategories];

    const indexIfExists = _collapsedCategories.indexOf(id);

    if (indexIfExists !== -1) {
      _collapsedCategories.splice(indexIfExists, 1);
    } else {
      _collapsedCategories.push(id);
    }

    setCollapsedCategories(_collapsedCategories);
  };

  const handleExpandAll = (): void => {
    setCollapsedCategories([]);
  };

  const handleCollapseAll = (): void => {
    setCollapsedCategories(categories.map((category) => category.mmId));
  };

  const hasBrands = useMemo(() => venueHasBrands(currentVenue?.menus ?? []), [currentVenue?.menus]);

  const displayName = hasBrands
    ? `${draftMenu?.apps[0].brand?.name ?? currentVenue?.name ?? ''} ${draftMenu?.name}`
    : currentVenue?.name;

  const submitMenu = useCallback(
    async (
      menuInput: MenuInput,
      publish: boolean
    ): Promise<{
      error: ApolloError;
      currentVenue: Venue | undefined;
    } | null> => {
      if (!draftMenu) return null;

      if (!currentVenue) {
        // eslint-disable-next-line no-console
        console.error('Expected to have current venue');
        return null;
      }

      let finalMenu: Menu = cloneDeep(draftMenu);
      const finalFailedAppIdsAndReqIds: { failedAppIds: string[]; requestIds: string[] } = {
        failedAppIds: [],
        requestIds: []
      };
      let returnError: { error: ApolloError; currentVenue: Venue | undefined } | null = null;
      const failedImageUpdates: QueuedProductImageUpdate[] = [];
      const impactedAppIds: string[] = [];

      // Save the menu with all new changes first
      await saveMenuMutation({
        variables: {
          venueId: currentVenue.hashedId,
          newMenu: menuInput,
          isSaveOnly: !publish
        },
        update: (cache, { data, extensions }) => {
          try {
            if (!data) {
              throw new Error('Expected result.data from saveMenu mutation');
            }
            const { saveMenu } = data;
            if (!saveMenu) {
              throw new Error('Expected result.data.saveMenu from saveMenu mutation');
            }

            const { failedAppIds, impactedApps: impactedAppsResult, menus: updatedMenus } = saveMenu;

            if (failedAppIds.length) {
              finalFailedAppIdsAndReqIds.failedAppIds = failedAppIds;
              finalFailedAppIdsAndReqIds.requestIds.push(extensions?.requestId);
            } else {
              const updatedMenu = updatedMenus?.find((menu) => menu.mmId === draftMenu.mmId);
              if (updatedMenu) {
                finalMenu = GraphqlTypecastWorkaround.toMenu(cloneDeep(updatedMenu));
              }
            }
            impactedAppIds.push(...impactedAppsResult.map((a: App) => a.id));
            setChangedCategoryIds([]);
          } catch (error) {
            // Note: Apollo Client update() does not propagate errors
            /* eslint-disable-next-line no-console */
            console.error('Error during saveMenu update():', error);
          }
        },
        onError: (error) => {
          returnError = { error, currentVenue };
        }
      });

      await Promise.all(
        queuedProductImageUpdates.map(async (imageUpdate): Promise<void> => {
          try {
            if (imageUpdate.image) {
              await updateProductImageMutation({
                variables: {
                  posId: imageUpdate.posId,
                  image: imageUpdate.image,
                  appIds: imageUpdate.appIds,
                  locationId: currentVenue.hashedId
                },
                update: (cache, { data, extensions }) => {
                  try {
                    if (!data) {
                      throw new Error('Expected result.data from updateProductImage mutation');
                    }
                    const { updateProductImage } = data;
                    if (!updateProductImage) {
                      throw new Error('Expected result.data.updateProductImage from updateProductImage mutation');
                    }

                    const { failedAppIds, newImageUrl } = updateProductImage;

                    if (failedAppIds.length) {
                      failedAppIds.forEach((failedAppId: string) => {
                        if (!finalFailedAppIdsAndReqIds.failedAppIds.includes(failedAppId))
                          finalFailedAppIdsAndReqIds.failedAppIds.push(failedAppId);
                      });
                      finalFailedAppIdsAndReqIds.requestIds.push(extensions?.requestId);
                    } else {
                      const clonedProducts = cloneDeep(finalMenu.products);
                      const updatedIndex = clonedProducts?.findIndex((product) => product.posId === imageUpdate.posId);
                      if (updatedIndex === undefined || updatedIndex === -1 || !clonedProducts?.length || !finalMenu) {
                        // eslint-disable-next-line no-console
                        console.error(`Product not found, cannot update cached imageUri for ${imageUpdate.posId}`);
                        return;
                      }
                      const updatedProduct = clonedProducts[updatedIndex];
                      updatedProduct.variablesByApp.forEach((vba) => (vba.imageUri = newImageUrl));
                      clonedProducts[updatedIndex] = updatedProduct;
                      finalMenu.products = clonedProducts;
                    }
                  } catch (error) {
                    // Note: Apollo Client update() does not propagate errors
                    /* eslint-disable-next-line no-console */
                    console.error('Error during updateProductImage update():', error);
                  }
                },
                onError: (error) => {
                  returnError = { error, currentVenue };
                }
              });
            } else {
              await deleteProductImageMutation({
                variables: {
                  posId: imageUpdate.posId,
                  appIds: imageUpdate.appIds,
                  locationId: currentVenue.hashedId
                },
                update: (cache, { data, extensions }) => {
                  try {
                    if (!data) {
                      throw new Error('Expected result.data from deleteProductImage mutation');
                    }
                    const { deleteProductImage } = data;
                    if (!deleteProductImage) {
                      throw new Error('Expected result.data.deleteProductImage from deleteProductImage mutation');
                    }

                    const { failedAppIds } = deleteProductImage;

                    if (failedAppIds.length) {
                      failedAppIds.forEach((failedAppId: string) => {
                        if (!finalFailedAppIdsAndReqIds.failedAppIds.includes(failedAppId))
                          finalFailedAppIdsAndReqIds.failedAppIds.push(failedAppId);
                      });
                      finalFailedAppIdsAndReqIds.requestIds.push(extensions?.requestId);
                    } else {
                      const clonedProducts = cloneDeep(finalMenu.products);
                      const updatedProduct = clonedProducts?.find((product) => product.posId === imageUpdate.posId);
                      if (!updatedProduct || !clonedProducts?.length || !finalMenu) {
                        // eslint-disable-next-line no-console
                        console.error(`Product not found, cannot delete cached imageUri for ${imageUpdate.posId}`);
                        return;
                      }
                      const updatedIndex = clonedProducts.findIndex((product) => product.posId === imageUpdate.posId);
                      updatedProduct.variablesByApp.forEach((vba) => (vba.imageUri = null));
                      clonedProducts[updatedIndex] = updatedProduct;
                      finalMenu.products = clonedProducts;
                    }
                  } catch (error) {
                    // Note: Apollo Client update() does not propagate errors
                    /* eslint-disable-next-line no-console */
                    console.error('Error during deleteProductImage update():', error);
                  }
                },
                onError: (error) => {
                  returnError = { error, currentVenue };
                }
              });
            }

            imageUpdate.appIds.forEach((imgAppId) => {
              if (!impactedAppIds.some((app) => app === imgAppId)) {
                impactedAppIds.push(imgAppId);
              }
            });
          } catch (error) {
            failedImageUpdates.push(imageUpdate);
            // eslint-disable-next-line no-console
            console.error('Failed to update product image', imageUpdate);
          }
        })
      );
      updateQueuedProductImageUpdates(failedImageUpdates);

      if (publish && !finalFailedAppIdsAndReqIds.failedAppIds.length && impactedAppIds.length) {
        await Promise.all(
          impactedAppIds.map(async (appId) => {
            await publishMenuMutation({
              variables: {
                venueId: currentVenue.hashedId,
                appId
              },
              onError: (error) => {
                if (!finalFailedAppIdsAndReqIds.failedAppIds.includes(appId)) {
                  finalFailedAppIdsAndReqIds.failedAppIds.push(appId);
                }
                const requestId =
                  error.graphQLErrors[0]?.extensions &&
                  'requestId' in error.graphQLErrors[0].extensions &&
                  typeof error.graphQLErrors[0].extensions.requestId === 'string'
                    ? error.graphQLErrors[0].extensions.requestId
                    : undefined;
                if (requestId) {
                  finalFailedAppIdsAndReqIds.requestIds.push(requestId);
                }
                // eslint-disable-next-line no-console
                console.error('Failed to publish menu');
                returnError = { error, currentVenue };
              },
              update: (_cache, { data }) => {
                if (data?.publishMenu) {
                  finalMenu.neverPublished = false;
                }
              }
            });
          })
        );
      }

      if (finalFailedAppIdsAndReqIds.failedAppIds.length) {
        const failedApps = finalFailedAppIdsAndReqIds.failedAppIds.reduce<AppFragment[]>((acc, appId) => {
          const cacheKey = cache.identify({ __typename: 'App', id: appId });
          if (!cacheKey) {
            // eslint-disable-next-line no-console
            console.error(`Unable to identify App with id ${appId}`);

            return acc;
          }
          const cachedFragment = cache.readFragment<AppFragment>({
            id: cacheKey,
            fragment: APP_FRAGMENT,
            fragmentName: 'AppFragment'
          });

          if (cachedFragment) {
            acc.push(cachedFragment);
          }

          return acc;
        }, []);

        const error = new ApolloError({
          extraInfo: {
            failedApps,
            subject: `Menu update Failing for ${listAppsInString(failedApps, hasBrands, currentVenue?.name)}`
          },
          graphQLErrors: [
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            {
              extensions: {
                mmCode: 6003,
                requestId: finalFailedAppIdsAndReqIds.requestIds.join(', ')
              }
            }
          ]
        });
        return { error, currentVenue };
      }
      if (returnError) return returnError;

      if (finalMenu) {
        const menuId = cache.identify(draftMenu);
        const menuData = cache.readFragment({
          id: menuId,
          fragment: MENU_FRAGMENT,
          fragmentName: 'MenuFragment'
        }) as Menu;
        const clonedMenuData = cloneDeep(menuData);

        clonedMenuData.categories = cloneDeep(finalMenu.categories);
        clonedMenuData.products = cloneDeep(finalMenu.products);
        clonedMenuData.neverPublished = finalMenu.neverPublished;

        cache.writeFragment({
          id: menuId,
          fragment: MENU_FRAGMENT,
          fragmentName: 'MenuFragment',
          data: clonedMenuData
        });

        setNewlyAddedPosProducts([]);
        setNewlyAddedPosProductImageMap({});
        setDraftMenus([finalMenu]);
        openSnackBar(
          publish
            ? `Published! Please allow a few minutes for your apps to reflect.`
            : `Saved! We’ll keep your menu safe until you’re ready to publish.`
        );
      }
      return null;
    },
    [
      draftMenu,
      publishMenuMutation,
      saveMenuMutation,
      queuedProductImageUpdates,
      updateQueuedProductImageUpdates,
      currentVenue,
      updateProductImageMutation,
      deleteProductImageMutation,
      hasBrands,
      cache,
      setDraftMenus,
      openSnackBar
    ]
  );

  const handlePublish = useCallback(() => {
    if (isOperatingHoursSupported && hasCommonHoursAcrossAllMenusBeenApplied) {
      openModal({
        type: 'COMMON_HOURS_CONFIRM',
        callback: () => {
          navigate(`/venue/${currentVenue?.hashedId}/hours`);
        }
      });
      return;
    }

    if (!draftMenu) return;
    const clonedMenu = cloneDeep(draftMenu);

    const invalidProducts = validateProductInputs(clonedMenu.products || []);
    if (invalidProducts.length) {
      openModal({
        type: 'INVALID_FIELDS_SINGLE_MENU_PUBLISH',
        items: invalidProducts.map(({ name }) => name),
        publish: true
      });
      return;
    }
    const menuInput = convertToMenuInput(
      clonedMenu,
      publishedMenu || undefined,
      newlyAddedPosProducts,
      newlyAddedPosProductImageMap
    );
    const menuChangeResult = compileChanges(menuInput, publishedMenu);
    openModal({
      type: 'PUBLISH_MENU',
      affectedItemsAndOptionsApps: menuChangeResult.otherAppsAffected,
      affectedApps: draftMenu.apps,
      callback: async () => await submitMenu(menuInput, true),
      firstPublish: neverPublished,
      hasBrands,
      venueName: currentVenue?.name,
      menu: draftMenu
    });
  }, [
    neverPublished,
    newlyAddedPosProducts,
    openModal,
    publishedMenu,
    draftMenu,
    submitMenu,
    hasBrands,
    currentVenue?.name,
    currentVenue?.hashedId,
    newlyAddedPosProductImageMap,
    isOperatingHoursSupported,
    hasCommonHoursAcrossAllMenusBeenApplied,
    navigate
  ]);

  const handleSave = useCallback(async (): Promise<{
    error: ApolloError;
    currentVenue: Venue | undefined;
  } | null> => {
    if (!draftMenu || !neverPublished) return null;

    const clonedMenu = cloneDeep(draftMenu);
    const invalidProducts = validateProductInputs(clonedMenu.products || []);
    if (invalidProducts.length) {
      openModal({
        type: 'INVALID_FIELDS_SINGLE_MENU_PUBLISH',
        items: invalidProducts.map(({ name }) => name),
        publish: false
      });
      return null;
    }
    const menuInput = convertToMenuInput(clonedMenu, publishedMenu || undefined, newlyAddedPosProducts);

    return await submitMenu(menuInput, false);
  }, [draftMenu, neverPublished, newlyAddedPosProducts, openModal, publishedMenu, submitMenu]);

  const [stickyHeaderOpen, setStickyHeaderOpen] = useState(false);

  const [isMobileView, setIsMobileView] = useState(innerWidth < WindowBreakpoints.SM);

  useEffect(() => {
    const isMobileViewNew = innerWidth < WindowBreakpoints.SM;
    if (isMobileViewNew !== isMobileView) setIsMobileView(isMobileViewNew);
  }, [innerWidth, isMobileView]);

  // Open/Close Sticky Header on changes (if not in Mobile)
  useEffect(() => {
    if (isMobileView) {
      if (stickyHeaderOpen) {
        setStickyHeaderOpen(false);
        closeStickyHeader();
      }
      return;
    }
    if (changedCategoryIds.length || changedProductIds.length) {
      if (!stickyHeaderOpen) setStickyHeaderOpen(true);
      openStickyHeader({
        text: neverPublished
          ? "To view your edits in the apps, head to the top of your menu and hit 'Publish' or 'Save' if you're not ready to go live."
          : "To see your changes in the apps, be sure to hit 'Publish'.",
        label: 'Publish banner',
        isVisibleHeight: isMobileView ? 350 : 150,
        primaryAction: {
          text: 'Publish',
          callback: async () => {
            const appToLog =
              draftMenu?.apps[0].id.includes('virtual') && draftMenu.apps[0].brand
                ? `${draftMenu.apps[0].name} - brand`
                : draftMenu?.apps[0].name || '';
            logAmplitudeEvent({
              name: 'click publish menu',
              category: AmplitudeEventCategories.MM_MENU,
              properties: {
                [AmplitudeEventProperties.MENU_ID]: draftMenu?.mmId || '',
                [AmplitudeEventProperties.MENU_NAME]: draftMenu?.name || '',
                [AmplitudeEventProperties.APP]: appToLog
              }
            });
            handlePublish();
          }
        },
        ...(neverPublished && {
          secondaryAction: {
            text: 'Save',
            callback: async () => {
              await handleSave();
            }
          }
        })
      });
    } else if (!changedCategoryIds.length && !changedProductIds.length && stickyHeaderOpen) {
      setStickyHeaderOpen(false);
      closeStickyHeader();
    }
  }, [
    changedCategoryIds.length,
    changedProductIds.length,
    closeStickyHeader,
    draftMenu,
    handlePublish,
    handleSave,
    isMobileView,
    neverPublished,
    openStickyHeader,
    stickyHeaderOpen
  ]);

  // Open/Close Snackbar on changes (if on Mobile)
  useEffect(() => {
    // remove if switch from mobile to desktop
    if (!isMobileView) {
      if (infoSnackBarId) {
        closeSnackBar(infoSnackBarId);
        setInfoSnackBarId(null);
      }
      return;
    }
    const text = neverPublished ? 'Unpublished menu' : 'Unpublished changes';
    // add if switch from unchanged to changed
    if ((changedCategoryIds.length || changedProductIds.length) && !infoSnackBarId) {
      setInfoSnackBarId(
        openSnackBar(
          text,
          undefined,
          () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }),
          SnackBarTypeEnum.INFO,
          -1
        )
      );
      return;
    }

    // remove if switch from changed to unchanged
    if (!changedCategoryIds.length && !changedProductIds.length && infoSnackBarId) {
      closeSnackBar(infoSnackBarId);
      setInfoSnackBarId(null);
    }
  }, [
    changedCategoryIds,
    changedProductIds,
    closeSnackBar,
    infoSnackBarId,
    isMobileView,
    itemId,
    menuId,
    neverPublished,
    openSnackBar
  ]);

  useEffect(() => {
    return () => {
      closeStickyHeader();
    };
  }, [closeStickyHeader]);

  useEffect(() => {
    return () => {
      if (infoSnackBarId) {
        closeSnackBar(infoSnackBarId);
        setInfoSnackBarId(null);
      }
    };
  }, [closeSnackBar, infoSnackBarId]);

  if (!draftMenu) {
    return <>No menu</>;
  }

  return (
    <CategoryContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        collapsedCategories,
        handleToggleCategoryCollapse,
        isLoading,
        changedCategoryIds,
        changedProductIds,
        addChangedCategoryIds,
        addNewInlineProduct,
        apps: draftMenu.apps,
        items,
        setItems,
        containers,
        setContainers,
        updateCategory,
        addNewCategory,
        deleteCategory,
        reorderCategories,
        reorderProducts,
        deleteProduct,
        menuIndex: currentMenuIndex
      }}
    >
      <div data-testid="single-menu">
        <MenuTitle
          menu={draftMenu}
          neverPublished={neverPublished}
          isLoading={isLoading}
          displayName={displayName}
          themeClassName={themeClassName}
          changesDetected={!!changedCategoryIds.length || !!changedProductIds.length}
          handlePublish={
            changedCategoryIds.length || changedProductIds.length || neverPublished
              ? () => {
                  const appToLog =
                    draftMenu?.apps[0].id.includes('virtual') && draftMenu.apps[0].brand
                      ? `${draftMenu.apps[0].name} - brand`
                      : draftMenu?.apps[0].name || '';
                  logAmplitudeEvent({
                    name: 'click publish menu',
                    category: AmplitudeEventCategories.MM_MENU,
                    properties: {
                      [AmplitudeEventProperties.MENU_ID]: draftMenu.mmId,
                      [AmplitudeEventProperties.MENU_NAME]: draftMenu.name,
                      [AmplitudeEventProperties.APP]: appToLog
                    }
                  });

                  handlePublish();
                }
              : undefined
          }
          handleSave={
            (changedCategoryIds.length || changedProductIds.length) && neverPublished
              ? () => {
                  return handleSave();
                }
              : undefined
          }
          isMobileView={isMobileView}
          busyUntil={busyUntil}
        />
        <DoshiiMenuLogic
          draftMenu={draftMenu}
          currentVenue={currentVenue}
          themeClassName={themeClassName}
          collapsedCategories={collapsedCategories}
          handleCollapseAll={handleCollapseAll}
          handleExpandAll={handleExpandAll}
        />
        {isTest && <TestDragSimulator menu={draftMenu} reset={resetDragOrder} />}
      </div>
    </CategoryContext.Provider>
  );
};

export default SingleMenu;
